import React from 'react';
import { AlertCircle, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@vaiu/ui/alert';
import { Button } from '@vaiu/ui/button';

interface DevelopmentNoticeProps {
  feature: string;
  onDismiss?: () => void;
}

export default function DevelopmentNotice({ feature, onDismiss }: DevelopmentNoticeProps) {
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    // Check if this notice was previously dismissed
    const dismissedNotices = JSON.parse(localStorage.getItem('dismissedNotices') || '{}');
    if (dismissedNotices[feature]) {
      setDismissed(true);
    }
  }, [feature]);

  const handleDismiss = () => {
    setDismissed(true);
    // Save dismissal to localStorage
    const dismissedNotices = JSON.parse(localStorage.getItem('dismissedNotices') || '{}');
    dismissedNotices[feature] = true;
    localStorage.setItem('dismissedNotices', JSON.stringify(dismissedNotices));
    if (onDismiss) {
      onDismiss();
    }
  };

  if (dismissed) {
    return null;
  }

  return (
    <Alert className="mb-4 border-yellow-200 bg-yellow-50">
      <AlertCircle className="h-4 w-4 text-yellow-600" />
      <AlertTitle className="text-yellow-800">Development Preview</AlertTitle>
      <AlertDescription className="text-yellow-700">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="mb-2">
              The {feature} feature is currently in development. The backend APIs are not yet implemented.
            </p>
            <p className="text-sm">
              This is a preview of the new unified token architecture that reduces API complexity by 91%. 
              The UI is fully functional, but data will be simulated until the platform team implements the consolidated endpoints.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="ml-4 -mt-2"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
